import { useNavigate, Routes, Route, NavLink, useMatch } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import TourLeaderHome from './TourLeaderHome';
import TourLeaderTourDetail from './TourLeaderTourDetail';
import TeamDatabasePage from '../staff/TeamDatabasePage';
import { MarketProvider } from '../../contexts/MarketContext';

export default function TourLeaderDashboard() {
  const { currentUser, logout, userRoles, isStaffOrAdmin, userPortals } = useAuth();
  const isStaffToo = isStaffOrAdmin || userRoles.includes('staff') || userRoles.includes('admin');
  const navigate = useNavigate();
  const isTourDetail = useMatch('/tour-leader/tours/:id');

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  const initials = currentUser?.email?.[0]?.toUpperCase() || '?';
  const displayName = currentUser?.displayName || currentUser?.email || '';

  // Tour detail owns the full screen — skip dashboard header and padding
  if (isTourDetail) {
    return (
      <Routes>
        <Route path="tours/:id" element={<TourLeaderTourDetail />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-page flex flex-col">

      {/* Topbar */}
      <header className="h-14 bg-navy flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-[8px] bg-ba flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-[12px] tracking-wide">MST</span>
          </div>
          <span className="text-white font-semibold text-[15px]">Tour Leader Portal</span>
          <span className="bg-white/15 text-white text-[11px] px-3 py-0.5 rounded-[20px]">
            Tour Leader
          </span>
        </div>

        <div className="flex items-center gap-3">
          {isStaffToo && (
            <button
              onClick={() => navigate('/staff')}
              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-[12px] font-semibold px-3 py-1.5 rounded-[8px] transition"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
              Ops view
            </button>
          )}
          <div className="flex items-center gap-2 bg-white/10 rounded-[20px] px-3 py-1.5">
            <span className="w-6 h-6 rounded-full bg-[#C2410C] flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0">
              {initials}
            </span>
            <span className="hidden sm:inline text-white text-[13px] max-w-[140px] truncate">{displayName}</span>
          </div>
          <button
            onClick={handleLogout}
            className="text-white/70 hover:text-white text-[13px] transition"
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Sub-nav */}
      <div className="bg-white border-b border-[#E5E7EB] px-4 flex gap-0">
        <NavLink
          to="/tour-leader" end
          className={({ isActive }) =>
            `px-4 py-3 text-[13px] font-semibold border-b-2 -mb-px transition ${isActive ? 'border-[#1A4A8A] text-[#1A4A8A]' : 'border-transparent text-[#6B7280] hover:text-[#374151]'}`
          }
        >
          My Tours
        </NavLink>
        <NavLink
          to="/tour-leader/team-database"
          className={({ isActive }) =>
            `px-4 py-3 text-[13px] font-semibold border-b-2 -mb-px transition ${isActive ? 'border-[#1A4A8A] text-[#1A4A8A]' : 'border-transparent text-[#6B7280] hover:text-[#374151]'}`
          }
        >
          Team Database
        </NavLink>
      </div>

      {/* Content */}
      <main className="flex-1 px-4 py-6 md:px-8 md:py-8 overflow-y-auto page-fade">
        <Routes>
          <Route index element={<TourLeaderHome />} />
          <Route path="tours/:id" element={<TourLeaderTourDetail />} />
          <Route path="team-database" element={
            <MarketProvider portals={userPortals}>
              <TeamDatabasePage />
            </MarketProvider>
          } />
        </Routes>
      </main>
    </div>
  );
}
